import {
  Pagination,
  Card,
  CardBody,
  Skeleton,
} from "@heroui/react";
import React from "react";
import { OpportunityViewModal } from "../../component/opportunities.tsx/view/opportunityViewModal";
import ViewModal from "../../component/readyRoster/view/viewModal";
import MarketplaceCard from "./marketCard";
import MarketplaceCardsFilters from "./marketplaceFilters";
import { useMarketplaceCards } from "./useMarketplaceCards";
import { MarketplaceCardResponseDto } from "../../types/types";

const MarketplaceCards: React.FC = () => {
  const {
    t,
    profileType,
    loginResponse,
    activeTab,
    currentMonth,
    setCurrentMonth,
    searchName,
    setSearchName,
    filterCardType,
    setFilterCardType,
    filterLocation,
    setFilterLocation,
    filterSport,
    setFilterSport,
    filterWeightClass,
    setFilterWeightClass,
    filterPurse,
    setFilterPurse,
    filterCurrency,
    setFilterCurrency,
    filterOpportunityType,
    setFilterOpportunityType,
    selectedRosterStatus,
    setSelectedRosterStatus,
    selectedTitle,
    setSelectedTitle,
    cards,
    loading,
    showReportPopup,
    setShowReportPopup,
    currentPage,
    setCurrentPage,
    totalPages,
    isViewModalOpen,
    selectedCard,
    viewCardId,
    isOpportunityViewOpen,
    setIsOpportunityViewOpen,
    isPublishing,
    confirmPublishPopup,
    setConfirmPublishPopup,
    confirmContactPopup,
    setConfirmContactPopup,
    locations,
    sports,
    weightClasses,
    purses,
    opportunityTypes,
    rosterStatus,
    fetchMarketplaceCards,
    handleResetFilters,
    handleViewModalClose,
    handlePublish,
    handleDelete,
    handleView,
    handleOpportunityView,
    handleCreateContact,
  } = useMarketplaceCards();

  return (
    <div className="w-full h-[90vh] flex flex-col bg-gray-50">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-3 flex-shrink-0">
        <div className="max-w-full mx-auto">
          <MarketplaceCardsFilters
            profileType={profileType}
            activeTab={activeTab}
            searchName={searchName}
            setSearchName={setSearchName}
            filterCardType={filterCardType}
            setFilterCardType={setFilterCardType}
            filterLocation={filterLocation}
            setFilterLocation={setFilterLocation}
            filterSport={filterSport}
            setFilterSport={setFilterSport}
            filterWeightClass={filterWeightClass}
            setFilterWeightClass={setFilterWeightClass}
            filterPurse={filterPurse}
            setFilterPurse={setFilterPurse}
            filterCurrency={filterCurrency}
            setFilterCurrency={setFilterCurrency}
            filterOpportunityType={filterOpportunityType}
            setFilterOpportunityType={setFilterOpportunityType}
            selectedRosterStatus={selectedRosterStatus}
            setSelectedRosterStatus={setSelectedRosterStatus}
            selectedTitle={selectedTitle}
            setSelectedTitle={setSelectedTitle}
            locations={locations}
            sports={sports}
            weightClasses={weightClasses}
            purses={purses}
            opportunityTypes={opportunityTypes}
            rosterStatus={rosterStatus}
            handleResetFilters={handleResetFilters}
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="max-w-full mx-auto p-3">
          {loading ? (
            <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(240px,1fr))] justify-center">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card
                  key={i}
                  className="w-full p-4 space-y-3"
                  radius="lg"
                  shadow="sm"
                >
                  <Skeleton className="rounded-lg">
                    <div className="h-24 bg-default-300 rounded-lg" />
                  </Skeleton>
                  <Skeleton className="w-4/5 rounded-lg">
                    <div className="h-4 bg-default-200 rounded-lg" />
                  </Skeleton>
                  <Skeleton className="w-3/5 rounded-lg">
                    <div className="h-3 bg-default-100 rounded-lg" />
                  </Skeleton>
                  <Skeleton className="w-2/5 rounded-full">
                    <div className="h-6 bg-default-100 rounded-full" />
                  </Skeleton>
                </Card>
              ))}
            </div>
          ) : cards.length > 0 ? (
            <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(240px,1fr))] justify-center">
              {cards.map((item: MarketplaceCardResponseDto) => {
                try {
                  if (item.readyRosterCard) {
                    return (
                      <MarketplaceCard
                        key={`roster-${item.id}`}
                        item={item}
                        role={profileType}
                        activeTab={activeTab}
                        onView={() => handleView(item)}
                      />
                    );
                  } else if (item.opportunityCardResponse) {
                    return (
                      <MarketplaceCard
                        key={`opportunity-${item.id}`}
                        item={item}
                        role={profileType}
                        activeTab={activeTab}
                        onView={() => handleOpportunityView(item.id)}
                      />
                    );
                  }
                  return null;
                } catch (error) {
                  return null;
                }
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-16 h-16 rounded-full bg-default-100 flex items-center justify-center">
                <svg
                  className="w-7 h-7 text-default-400"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H6.911a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661Z"
                  />
                </svg>
              </div>
              <p className="text-default-400 text-sm">{t("no_cards_found")}</p>
            </div>
          )}

          {cards.length > 0 && (
            <div className="fixed bottom-0 right-0 left-[80px] flex flex-col items-center justify-center py-4 z-50">
              <Pagination
                total={totalPages}
                page={currentPage}
                onChange={(page) => setCurrentPage(page)}
                showControls
                color="primary"
                size="sm"
              />
            </div>
          )}
        </div>
      </div>

      {selectedCard && (
        <ViewModal
          isOpen={isViewModalOpen}
          onClose={handleViewModalClose}
          viewCard={selectedCard}
          marketplaceId={selectedCard.id}
          role={profileType}
          activeTab={activeTab}
          loggedInUserProfileType={profileType}
          loggedInUserName={loginResponse?.username || ""}
          currentMonth={currentMonth}
          setCurrentMonth={setCurrentMonth}
          selectedDates={
            new Set<string>(selectedCard?.readyRosterCard?.availableDays || [])
          }
          fetchMarketplaceCards={fetchMarketplaceCards}
          isPublishing={isPublishing}
          confirmPublishPopup={confirmPublishPopup}
          setConfirmPublishPopup={setConfirmPublishPopup}
          confirmContactPopup={confirmContactPopup}
          setConfirmContactPopup={setConfirmContactPopup}
          showReportPopup={showReportPopup}
          setShowReportPopup={setShowReportPopup}
          handlePublish={handlePublish}
          handleCreateContact={handleCreateContact}
        />
      )}

      {viewCardId && (
        <OpportunityViewModal
          isOpen={isOpportunityViewOpen}
          onClose={() => setIsOpportunityViewOpen(false)}
          cardId={viewCardId}
          activeTab={activeTab}
          onRefresh={fetchMarketplaceCards}
        />
      )}
    </div>
  );
};

export default MarketplaceCards;
